/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { dialogComponents } from 'components/Dialog/dialogComponents';
import { useModalActions } from 'components/ModalManager/Context';
import { Select } from 'components/Select';
import React, { useCallback, useMemo, useState } from 'react';
import { Button } from 'components/Button';
import { baseTheme } from 'styles/utils';
import { checkIfPatHasComputeAccess, getProviderData, getDeployModelRequestBody, createFourDigitRandomString } from 'modules/Compute/utils';
import { noop } from 'utils/fp';
import { nodepoolsApiEffects } from 'api/nodepools/apiEffects';
import { useCurrentUserId, useCurrentUser } from 'modules/Auth/hooks';
import { CreateNodepoolBody } from 'api/nodepools/types';
import { useMutation, useQueryClient } from 'react-query';
import { useFeatureFlagsLD } from 'modules/FeatureFlagsLD/FeatureFlagsProviderLD';
import { clustersApiEffects } from 'api/computeClusters/apiEffects';
import { ComputeClusters } from 'api/computeClusters/types';
import cogoToast from 'components/ToastMessage/ToastMessage';
import { useRouter } from 'next/router';
import { DeploymentFormValues } from 'modules/Compute/types';
import { deploymentsApiEffects } from 'api/deployments/apiEffects';
import { InfoMessage } from 'components/InfoMessage';
import { IconInfo } from 'components/Icons';
import { ComputeDetails, FormatOption } from 'modules/Home/components/SetupComputeModal';
import { getNewPATDescription, NodePoolDetails, SetupComputeMockData, SetupComputeType, TrendingModel } from 'modules/Home/constants';
import {
  description,
  detailsText,
  infoMessageStyles,
  link,
  selectStyles,
  titleStyles,
  titleWrapper,
  modalFooter,
} from 'modules/Home/components/SetupComputeModal.styles';
import { RestrictBillingPlan, UpgradePlanWrapper } from 'modules/Home/components/RestrictBillingPlan';
import { usePATOptions } from 'modules/Home/components/usePATOptions';
import { FormatOptionForPAT } from 'modules/Home/components/FormatOptionForPAT';
import { generateDeployNewModelUrl } from 'modules/Compute/Components/ComputeEntitySelector/utils';
import { useDataCollection } from 'modules/AnalyticsCollection/AnalyticsCollectionProvider';
import { HUBSPOT_CUSTOM_EVENTS_NAME, sendEvent } from 'utils/hubspotApi/hubspotApi';

type SetupComputeAndDeployModalProps = {
  model: TrendingModel;
  closeAction: () => void;
};

const SETUP_COMPUTE_AND_DEPLOY_MODAL_ID = 'setup_compute_and_deploy_modal';

export function SetupComputeAndDeployModal({ model, closeAction }: SetupComputeAndDeployModalProps) {
  const [computeType, setComputeType] = useState(model.supportedComputeTypes[0]);
  const { nodePoolsRestrictInstancesLDFF } = useFeatureFlagsLD();
  const { track } = useDataCollection();
  const user = useCurrentUser();
  const userId = useCurrentUserId();
  const router = useRouter();
  const queryClient = useQueryClient();
  const {
    patOptions,
    selectedPATOption,
    setSelectedPatOption,
    createNewPAT,
    isLoading: isPATLoading,
  } = usePATOptions({
    userId: userId as string,
    newPatDescription: getNewPATDescription(computeType),
    filterPAT: checkIfPatHasComputeAccess,
  });

  const computeTypeDetails = useMemo(() => SetupComputeMockData.find((data) => data.setupComputeType === computeType), [computeType]);
  const nodepoolDetails = useMemo(
    () => ({ ...computeTypeDetails?.nodepoolDetails!, ...(nodePoolsRestrictInstancesLDFF ? { minInstances: 0 } : {}) }),
    [nodePoolsRestrictInstancesLDFF, computeTypeDetails],
  );

  // Copying some code from: modules/User/components/Settings/ComputeClusters/ComputeClusterForm/utils/hooks.ts
  const { mutate, isLoading } = useMutation(
    async ({ computeType, nodepoolDetails }: { computeType: SetupComputeType; nodepoolDetails: NodePoolDetails }) => {
      const computeTypeSlug = computeType?.toLowerCase();
      const clusterId = `${computeTypeSlug}-cluster-${createFourDigitRandomString()}`;
      const nodepoolId = `${computeTypeSlug}-nodepool-${createFourDigitRandomString()}`;
      const deploymentId = `${computeTypeSlug}-deploy-${createFourDigitRandomString()}`;

      const keyId = await createNewPAT();

      const computeClusterBody: ComputeClusters = {
        compute_clusters: [
          {
            id: clusterId,
            cloud_provider: {
              id: nodepoolDetails.providerId,
              name: getProviderData(nodepoolDetails.providerId, nodepoolDetails.regionId)[0].name,
            },
            region: nodepoolDetails.regionId,
            cluster_type: 'dedicated',
            managed_by: 'clarifai',
            user_id: userId,
            key: { id: keyId as string },
            visibility: {
              gettable: 10,
            },
          },
        ],
      };

      const createNodepoolBody: CreateNodepoolBody = {
        nodepools: [
          {
            id: nodepoolId,
            node_capacity_type: {
              capacity_types: [1],
            },
            instance_types: [
              {
                id: nodepoolDetails.instanceId,
              },
            ] as any,

            min_instances: nodepoolDetails.minInstances,
            max_instances: nodepoolDetails.maxInstances,
            visibility: {
              gettable: 10,
            },
          },
        ],
      };

      const deployModelBody = getDeployModelRequestBody({
        isEditing: false,
        userId,
        values: {
          clusterId: { value: clusterId, name: clusterId },
          nodePoolId: { value: nodepoolId, name: nodepoolId },
          model: { id: model.modelId, app_id: model.appId, user_id: model.userId } as CF.API.Models.Model,
          deploymentId,
          deployLatestVersion: true,
        } as DeploymentFormValues,
      });

      const isClusterCreated = await clustersApiEffects.createCluster(userId as string, computeClusterBody);
      if (!isClusterCreated) {
        return null;
      }

      cogoToast.success(`Cluster ${clusterId} created successfully.`);
      track('Home Page -> New Cluster Created', {
        clusterId,
        compute_type: computeType,
        compute_region: nodepoolDetails.regionId,
        compute_provider: nodepoolDetails.providerId,
      });
      sendEvent({
        email: user?.email,
        eventName: HUBSPOT_CUSTOM_EVENTS_NAME.coClusterCreated,
        properties: {
          compute_type: computeType,
          compute_region: nodepoolDetails.regionId,
          compute_provider: nodepoolDetails.providerId,
        },
      });

      const isNodepoolCreated = await nodepoolsApiEffects.createNodepool(userId, clusterId, createNodepoolBody);
      if (!isNodepoolCreated) {
        return null;
      }
      cogoToast.success(`Nodepool ${nodepoolId} created successfully.`);
      track('Home Page -> New Nodelpool Created', { clusterId, nodepoolId });

      queryClient.invalidateQueries(['MyCompute', { userOrOrgId: userId }]);

      const isModelDeployed = await deploymentsApiEffects.createDeployment(userId, deployModelBody);
      if (!isModelDeployed) {
        return null;
      }

      return { clusterId, nodepoolId, deploymentId };
    },
    {
      onSuccess: (data) => {
        if (data) {
          cogoToast.success(`Model deployed successfully.`);
          track('Home Page -> Model deployed successfully', {
            clusterId: data.clusterId,
            nodepoolId: data.nodepoolId,
            compute_type: computeType,
            compute_region: nodepoolDetails.regionId,
            compute_provider: nodepoolDetails.providerId,
            model_id: model.modelId,
            model_user_id: model.userId,
            model_version_id: model.modelVersionId,
          });
          sendEvent({
            email: user?.email,
            eventName: HUBSPOT_CUSTOM_EVENTS_NAME.coModelDeployed,
            properties: {
              compute_type: computeType,
              compute_region: nodepoolDetails.regionId,
              compute_provider: nodepoolDetails.providerId,
              model_id: model.modelId,
              model_user_id: model.userId,
              model_version_id: model.modelVersionId,
            },
          });
          router.push(`/compute/clusters/${encodeURIComponent(data.clusterId)}/nodepools/${data.nodepoolId}`);
        }
      },
    },
  );

  return (
    <RestrictBillingPlan
      requiredScopes={computeTypeDetails?.requiredScopes}
      requiredFeaturedFlags={computeTypeDetails?.requiredFeaturedFlags}
      closeAction={closeAction}
    >
      <div>
        <div className={titleWrapper}>
          <h3 className={titleStyles}>Deploy {model.modelId}</h3>
          <dialogComponents.CloseButton onClick={closeAction} />
        </div>
        <p className={description}>Review your compute instance details before proceeding.</p>
        <Select
          modal
          title={<FormatOption setupComputeType={computeType} />}
          onChange={({ value }) => setComputeType(value as SetupComputeType)}
          options={[
            {
              id: SetupComputeType.Basic,
              name: 'Basic Compute',
              value: SetupComputeType.Basic,
              content: <FormatOption setupComputeType={SetupComputeType.Basic} />,
            },
            {
              id: SetupComputeType.Advanced,
              name: 'Advanced Compute',
              value: SetupComputeType.Advanced,
              content: <FormatOption setupComputeType={SetupComputeType.Advanced} />,
            },
          ].filter((option) => model.supportedComputeTypes.includes(option.id))}
          className={selectStyles}
        />

        {!!userId && (
          <Select
            modal
            title={<FormatOptionForPAT pat={selectedPATOption ?? ''} isLoading={isPATLoading} />}
            onChange={({ value }) => setSelectedPatOption(value as SetupComputeType)}
            options={patOptions}
            className={selectStyles}
          />
        )}

        <ComputeDetails computeType={computeType} nodepoolDetails={nodepoolDetails} />

        <InfoMessage className={infoMessageStyles}>
          <IconInfo color={baseTheme.light.colors.gray500} size={18} style={{ minWidth: '1.125rem' }} />
          <p className={detailsText} style={{ marginBottom: 0, maxWidth: 500 }}>
            Want to use an existing cluster and nodepool to deploy {model.modelId} ? Then{' '}
            <a className={link} href={modelDeploymentPageLink(model)}>
              click here.
            </a>
          </p>
        </InfoMessage>

        <div className={modalFooter}>
          <Button size="md" variant="tertiary" onClick={closeAction}>
            Cancel
          </Button>
          <UpgradePlanWrapper>
            <Button size="md" loading={isLoading} disabled={isLoading} onClick={() => mutate({ computeType, nodepoolDetails })}>
              Deploy
            </Button>
          </UpgradePlanWrapper>
        </div>
      </div>
    </RestrictBillingPlan>
  );
}

export function useSetupComputeAndDeployModal() {
  const { openModal, closeModal } = useModalActions();

  return useCallback((props: Omit<SetupComputeAndDeployModalProps, 'closeAction'>) => {
    openModal({
      id: SETUP_COMPUTE_AND_DEPLOY_MODAL_ID,
      title: '',
      content: <SetupComputeAndDeployModal {...props} closeAction={() => closeModal({ id: SETUP_COMPUTE_AND_DEPLOY_MODAL_ID })} />,
      makeActions: noop,
    });
  }, []);
}

function modelDeploymentPageLink({ modelId, appId, userId }: TrendingModel) {
  return generateDeployNewModelUrl({ modelId, appId, userId });
}
